// Dedicated location pages for SEO. Each entry produces a route at
// /live-in-care/:slug — Google + local "near me" searches will index these.
// Copy is intentionally unique per town to avoid duplicate-content issues.

export const AREAS = [
  {
    slug: "epping",
    name: "Epping",
    postcode: "CM16",
    headline: "Live-in care for elders in Epping.",
    intro:
      "We're a family-run, CQC-rated Good live-in care company based right on your doorstep in Epping — providing premium in-home care for elders across the town and the surrounding villages.",
    why: [
      "Our office and senior team are local to Epping — we know the GP surgeries, the pharmacies on the High Street, and the route from your front door to St Margaret's.",
      "We've placed live-in carers for elderly residents on Bell Common, Coopersale, Theydon Bois, Hemnall Street, and the lanes around Epping Forest itself.",
      "Most Epping families can start with us within 1–2 weeks, or within 48 hours for urgent hospital discharges from PAH or Whipps Cross.",
    ],
    nearby: ["Theydon Bois", "Loughton", "North Weald", "Coopersale", "Ongar"],
  },
  {
    slug: "loughton",
    name: "Loughton",
    postcode: "IG10",
    headline: "Live-in care for elders in Loughton.",
    intro:
      "From the Broadway to Debden, our live-in carers for the elderly support Loughton families who want their parents to grow old at home — not in a care home. CQC Rated Good. Same trusted carer, every day.",
    why: [
      "Loughton has one of the largest populations of over-75s in West Essex. We've supported families on Forest Road, Goldings Hill, Smarts Lane and along the High Road.",
      "We coordinate closely with Loughton's GP surgeries, district nurses and Whipps Cross Hospital for hospital-discharge live-in care for the elderly.",
      "Visiting and overnight care available too if live-in care is too much, too soon — we'll guide you to the right level.",
    ],
    nearby: ["Buckhurst Hill", "Chigwell", "Theydon Bois", "Epping", "Debden"],
  },
  {
    slug: "theydon-bois",
    name: "Theydon Bois",
    postcode: "CM16",
    headline: "Live-in care for elders in Theydon Bois.",
    intro:
      "Theydon Bois is a village that looks after its own — and so do we. Bespoke, premium live-in care for elderly residents who want to stay in the home they've loved for decades.",
    why: [
      "We provide live-in carers for elderly Theydon families around The Green, Coppice Row, Forest Drive and Loughton Lane.",
      "Our carers are matched on personality and interests, not just shifts — so the same warm, familiar face is there every day.",
      "Family-run and deeply local: we live within 15 minutes of you.",
    ],
    nearby: ["Epping", "Loughton", "Coopersale", "North Weald", "Abridge"],
  },
  {
    slug: "harlow",
    name: "Harlow",
    postcode: "CM17–CM20",
    headline: "Live-in care for elders in Harlow.",
    intro:
      "From Old Harlow to Church Langley, we provide premium one-to-one in-home care for elders across the whole of Harlow — so your loved one can stay in the neighbourhood they raised their family in.",
    why: [
      "We coordinate with Princess Alexandra Hospital (PAH) for fast-turnaround discharge live-in care for elderly patients heading home.",
      "Live-in carers placed across Old Harlow, Church Langley, Newhall, Sumners, Potter Street and Mark Hall.",
      "Couples' rates available — keep Mum and Dad together at home, with one live-in carer for both.",
    ],
    nearby: ["Old Harlow", "Roydon", "Sawbridgeworth", "Nazeing", "Hatfield Heath"],
  },
  {
    slug: "chigwell",
    name: "Chigwell",
    postcode: "IG7",
    headline: "Live-in care for elders in Chigwell.",
    intro:
      "Chigwell families come to us when they want care that fits the standard of the home — discreet, premium, one-to-one live-in care for elders, delivered by a hand-picked carer who arrives quietly and stays.",
    why: [
      "Live-in carers for elderly residents on Chigwell Row, Manor Road, Hainault Road and the lanes off Roding Lane.",
      "Discreet, dignified, and matched on values as much as skill.",
      "Often a more elegant alternative to a private care home — and frequently more affordable than two part-time carers.",
    ],
    nearby: ["Buckhurst Hill", "Loughton", "Woodford Green", "Chigwell Row", "Hainault"],
  },
  {
    slug: "buckhurst-hill",
    name: "Buckhurst Hill",
    postcode: "IG9",
    headline: "Live-in care for elders in Buckhurst Hill.",
    intro:
      "Buckhurst Hill is small, leafy, and well-connected — and so is our live-in care here. We provide warm, full-time in-home care for elders so your parent can stay in the home and community they belong to.",
    why: [
      "Live-in carers placed for elderly Buckhurst Hill clients around Queens Road, Roebuck Lane, Albert Road and the Princes Road area.",
      "Direct, easy contact with Whipps Cross and Whipps Cross GP surgeries.",
      "Family-run — our founders still answer the phone.",
    ],
    nearby: ["Loughton", "Chigwell", "Woodford Green", "Debden", "Theydon Bois"],
  },
  {
    slug: "ongar",
    name: "Ongar",
    postcode: "CM5",
    headline: "Live-in care for elders in Ongar.",
    intro:
      "From High Ongar to Chipping Ongar, our live-in carers for elderly residents bring premium home care to one of Essex's most beautiful rural areas — so country life doesn't have to end with retirement.",
    why: [
      "Carers placed for elderly Ongar residents along the High Street, Castle Street, Greensted Road and across the surrounding hamlets.",
      "Comfortable working in larger countryside properties, including gardens and pets.",
      "Couples-care available — one carer, both of you, at home.",
    ],
    nearby: ["North Weald", "Epping", "Stondon Massey", "Blackmore", "Fyfield"],
  },
  {
    slug: "waltham-abbey",
    name: "Waltham Abbey",
    postcode: "EN9",
    headline: "Live-in care for elders in Waltham Abbey.",
    intro:
      "Waltham Abbey families want care with the same warmth as the town itself. Our live-in carers for elderly clients bring premium, one-to-one in-home care to homes across the EN9 area.",
    why: [
      "Live-in care placements across Roundhills, Sewardstone Road, Sun Street and Highbridge Street.",
      "Quick access to Whipps Cross and the Princess Alexandra Hospital for discharges.",
      "Same carer every visit. No shift roulette.",
    ],
    nearby: ["Epping", "Loughton", "Nazeing", "Cheshunt", "Lower Nazeing"],
  },
  {
    slug: "north-weald",
    name: "North Weald",
    postcode: "CM16",
    headline: "Live-in care for elders in North Weald.",
    intro:
      "Just up the road from our base, North Weald is one of the villages we know best. We provide warm, premium live-in care for elderly residents who want to stay in the home they've worked a lifetime for.",
    why: [
      "Live-in carers placed for elderly clients around High Road, Tylers Green, Vicarage Lane and the lanes off the old airfield.",
      "We coordinate easily with Epping GP surgeries and the local district nursing team.",
      "Comfortable in larger countryside properties — happy with dogs, cats and the odd visiting fox.",
    ],
    nearby: ["Epping", "Theydon Bois", "Ongar", "Tylers Green", "Hastingwood"],
  },
  {
    slug: "old-harlow",
    name: "Old Harlow",
    postcode: "CM17",
    headline: "Live-in care for elders in Old Harlow.",
    intro:
      "The conservation cottages and tree-lined streets of Old Harlow are a beautiful place to grow old. Our live-in carers for elderly Old Harlow residents help your loved one stay rooted in the place they belong.",
    why: [
      "Live-in care for elderly residents around Mulberry Green, Churchgate Street, Sheering Road and Market Street.",
      "Direct contact with Princess Alexandra Hospital (PAH) for rapid post-discharge live-in care.",
      "Couples' care: keep Mum and Dad together at home with one live-in carer for both.",
    ],
    nearby: ["Harlow", "Sawbridgeworth", "Sheering", "Roydon", "Hatfield Heath"],
  },
  {
    slug: "roydon",
    name: "Roydon",
    postcode: "CM19",
    headline: "Live-in care for elders in Roydon.",
    intro:
      "Roydon is a village that knows how to look after its own. We provide premium, one-to-one live-in carers for elderly residents — for families who want their parent supported in the home and community they've always known.",
    why: [
      "Live-in care placements around the High Street, Epping Road, Tylers Cross and the lanes beside the Lea Valley.",
      "Familiar with the Roydon GP, district nurses and the local pharmacy.",
      "Rural-experienced carers — comfortable with gardens, pets, smallholdings and the odd horse.",
    ],
    nearby: ["Harlow", "Nazeing", "Stanstead Abbotts", "Hunsdon", "Lower Nazeing"],
  },
  {
    slug: "nazeing",
    name: "Nazeing",
    postcode: "EN9",
    headline: "Live-in care for elders in Nazeing.",
    intro:
      "From Lower Nazeing to Middle Street, our live-in carers for elderly Nazeing residents bring premium in-home care to one of the loveliest pockets of the Lea Valley — so country retirement stays as country as ever.",
    why: [
      "Live-in care for elderly clients across Lower Nazeing, Upper Nazeing, Middle Street and Hoe Lane.",
      "Comfortable in detached and rural homes, with extensive gardens or annexes.",
      "Easy reach to Princess Alexandra Hospital and the Cheshunt medical hubs.",
    ],
    nearby: ["Waltham Abbey", "Roydon", "Broxbourne", "Lower Nazeing", "Hoddesdon"],
  },
  {
    slug: "sawbridgeworth",
    name: "Sawbridgeworth",
    postcode: "CM21",
    headline: "Live-in care for elders in Sawbridgeworth.",
    intro:
      "Sawbridgeworth has a quietly affluent, village feel — and our live-in care fits perfectly. Discreet, premium one-to-one in-home care for elders, delivered by a carer who blends gently into the rhythm of your loved one's life.",
    why: [
      "Live-in carers for elderly Sawbridgeworth residents around Bell Street, Knight Street, Cambridge Road and London Road.",
      "Close working relationship with the surgery on Knight Street and Bishop's Stortford community nurses.",
      "Many of our carers live nearby — so emergency cover is genuinely fast.",
    ],
    nearby: ["Old Harlow", "Harlow", "Bishop's Stortford", "Hatfield Heath", "Sheering"],
  },
  {
    slug: "hatfield-heath",
    name: "Hatfield Heath",
    postcode: "CM22",
    headline: "Live-in care for elders in Hatfield Heath.",
    intro:
      "Hatfield Heath is a village in the truest sense — green, close-knit, easy. Our live-in carers for elderly residents here help your parent stay part of the village they raised their family in.",
    why: [
      "Live-in care for elderly clients around The Heath, Friars Lane, White Roding Road and Stortford Road.",
      "Used to working in characterful older homes, with stairs, low beams and unique layouts.",
      "Comfortable supporting active retirees who still want to walk, garden and host the family.",
    ],
    nearby: ["Sawbridgeworth", "Harlow", "Hatfield Broad Oak", "Matching Tye", "Bishop's Stortford"],
  },
  {
    slug: "matching-tye",
    name: "Matching Tye",
    postcode: "CM17",
    headline: "Live-in care for elders in Matching Tye.",
    intro:
      "Matching Tye is small, quiet and beautifully Essex. We provide premium live-in care for elderly residents in the village and the surrounding hamlets — bringing one-to-one in-home support to people who'd never dream of leaving.",
    why: [
      "Live-in carers placed around The Green, Bury Road, Old Houseley Lane and the cottages off the High Laver Road.",
      "We know the local GP, the lanes, and the way the village ticks.",
      "Comfortable in larger detached homes — and very happy with dogs in residence.",
    ],
    nearby: ["Hatfield Heath", "Harlow", "Old Harlow", "Matching Green", "Sheering"],
  },
  {
    slug: "bishops-stortford",
    name: "Bishop's Stortford",
    postcode: "CM23",
    headline: "Live-in care for elders in Bishop's Stortford.",
    intro:
      "From the Causeway to Thorley and Hockerill, we provide premium one-to-one live-in care for elderly Bishop's Stortford residents — CQC Rated Good, and delivered by carers matched to your parent, not to a shift pattern.",
    why: [
      "Live-in carers placed for elderly clients across Thorley Park, Hockerill, Rye Street, All Saints, Havers Lane and the villages north of the town.",
      "Coordinate closely with Herts and Essex Hospital and Bishop's Stortford GP surgeries for hospital-discharge live-in care.",
      "Couples' care available — keep Mum and Dad together at home with one live-in carer for both.",
    ],
    nearby: ["Sawbridgeworth", "Stansted Mountfitchet", "Much Hadham", "Manuden", "Birchanger"],
  },
  {
    slug: "stansted-mountfitchet",
    name: "Stansted Mountfitchet",
    postcode: "CM24",
    headline: "Live-in care for elders in Stansted Mountfitchet.",
    intro:
      "Stansted Mountfitchet families come to us for premium live-in care that lets a much-loved parent stay in the village they've called home for decades — with the same warm carer, every single day.",
    why: [
      "Live-in carers for elderly clients around Chapel Hill, Silver Street, Cambridge Road and the lanes around the castle.",
      "Familiar with local GPs and easy access to Herts and Essex Hospital and Princess Alexandra Hospital.",
      "Rural-experienced carers — comfortable with country properties, gardens and pets.",
    ],
    nearby: ["Bishop's Stortford", "Birchanger", "Manuden", "Ugley", "Takeley"],
  },
  {
    slug: "much-hadham",
    name: "Much Hadham",
    postcode: "SG10",
    headline: "Live-in care for elders in Much Hadham.",
    intro:
      "One of Hertfordshire's most beautiful villages deserves care to match. Our live-in carers for elderly Much Hadham residents bring discreet, premium in-home support — so your parent can stay in the home they love.",
    why: [
      "Live-in care placements around High Street, Green Tye, Perry Green and the lanes toward Widford.",
      "Comfortable in listed properties, characterful older homes and larger rural houses.",
      "Close working relationship with the Much Hadham surgery and Bishop's Stortford community nurses.",
    ],
    nearby: ["Little Hadham", "Bishop's Stortford", "Ware", "Widford", "Perry Green"],
  },
  {
    slug: "little-hadham",
    name: "Little Hadham",
    postcode: "SG11",
    headline: "Live-in care for elders in Little Hadham.",
    intro:
      "Little Hadham is quiet, green and gently country. Our premium live-in carers for elderly residents keep the pace of village life exactly as it should be — warm, familiar and unhurried.",
    why: [
      "Live-in carers for elderly clients around The Ford, Church End, Bury Green and Cradle End.",
      "Comfortable with rural properties, garden maintenance and the odd resident dog or two.",
      "Easy reach to Bishop's Stortford GPs and Herts and Essex Hospital.",
    ],
    nearby: ["Much Hadham", "Bishop's Stortford", "Standon", "Albury", "Furneux Pelham"],
  },
  {
    slug: "manuden",
    name: "Manuden",
    postcode: "CM23",
    headline: "Live-in care for elders in Manuden.",
    intro:
      "Manuden is one of those villages families move to and never leave. We provide premium live-in care for elderly Manuden residents so the story continues in the home they've built a life in.",
    why: [
      "Live-in care placements around The Street, Mallows Green, Peakes End and the lanes toward Berden.",
      "Comfortable in listed and larger country homes with characterful stairs and layouts.",
      "Same familiar carer every day — no shift roulette, no strangers.",
    ],
    nearby: ["Bishop's Stortford", "Stansted Mountfitchet", "Berden", "Ugley", "Furneux Pelham"],
  },
  {
    slug: "furneux-pelham",
    name: "Furneux Pelham",
    postcode: "SG9",
    headline: "Live-in care for elders in Furneux Pelham.",
    intro:
      "Furneux Pelham and the Pelham villages are the very picture of English country retirement. Our live-in carers for elderly residents help your parent enjoy every quiet Sunday of it.",
    why: [
      "Live-in carers placed across the three Pelhams — Furneux, Stocking and Brent — and the surrounding hamlets.",
      "Comfortable in rural properties with gardens, orchards and the occasional livestock arrangement.",
      "Local coordination with the Brent Pelham surgery and Bishop's Stortford district nursing team.",
    ],
    nearby: ["Little Hadham", "Manuden", "Berden", "Braughing", "Stocking Pelham"],
  },
  {
    slug: "birchanger",
    name: "Birchanger",
    postcode: "CM23",
    headline: "Live-in care for elders in Birchanger.",
    intro:
      "Birchanger is a small, close-knit village right on the edge of Bishop's Stortford. Our live-in carers for elderly Birchanger residents deliver premium one-to-one care in the home your parent has always known.",
    why: [
      "Live-in care for elderly clients around Birchanger Lane, Church Street, Duckling Lane and Hall Road.",
      "Direct, easy access to Herts and Essex Hospital and Bishop's Stortford GP surgeries.",
      "Comfortable in village cottages and the newer developments off the A120.",
    ],
    nearby: ["Bishop's Stortford", "Stansted Mountfitchet", "Takeley", "Elsenham", "Little Hallingbury"],
  },
  {
    slug: "takeley",
    name: "Takeley",
    postcode: "CM22",
    headline: "Live-in care for elders in Takeley.",
    intro:
      "From Takeley Street to Little Canfield, our live-in carers for elderly Takeley residents bring warm, one-to-one premium home care — so retirement stays firmly in the home your parent chose.",
    why: [
      "Live-in care placements around The Street, Brewers End, Broadfields and the lanes toward Hatfield Broad Oak.",
      "Comfortable in country homes, new-build family properties and everything in between.",
      "Easy access to Princess Alexandra Hospital and the Great Dunmow surgeries.",
    ],
    nearby: ["Birchanger", "Hatfield Broad Oak", "Little Canfield", "Great Dunmow", "Stansted Mountfitchet"],
  },
  {
    slug: "sheering",
    name: "Sheering",
    postcode: "CM22",
    headline: "Live-in care for elders in Sheering.",
    intro:
      "Sheering is a quietly lovely village between Harlow and Bishop's Stortford — and one we know well. Our live-in carers for elderly Sheering residents deliver premium, one-to-one home care with warmth and continuity.",
    why: [
      "Live-in carers for elderly clients around The Street, Sheering Lower Road, Newman's End and the lanes toward Hatfield Heath.",
      "Familiar with the Sheering surgery, Princess Alexandra Hospital and Herts and Essex Hospital.",
      "Comfortable in older village properties as well as newer detached homes.",
    ],
    nearby: ["Hatfield Heath", "Old Harlow", "Sawbridgeworth", "Harlow", "Matching Tye"],
  },
  {
    slug: "farnham",
    name: "Farnham",
    postcode: "CM23",
    headline: "Live-in care for elders in Farnham (Essex).",
    intro:
      "Farnham is one of the quietest, prettiest villages north of Bishop's Stortford. Our live-in carers for elderly Farnham residents bring calm, premium one-to-one care to homes across the village and the lanes around it.",
    why: [
      "Live-in care for elderly clients around The Chase, Hazel End, Church Road and the surrounding farmland.",
      "Rural-experienced carers — happy with dogs, cats, gardens and country routines.",
      "Easy reach to Bishop's Stortford GPs and Herts and Essex Hospital.",
    ],
    nearby: ["Bishop's Stortford", "Manuden", "Rickling", "Stansted Mountfitchet", "Ugley"],
  },
];

// Convenience lookup
export const findArea = (slug) => AREAS.find((a) => a.slug === slug);
